import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom'
import { Button } from '../ui/button'
import DarkModeToggle from '../ui/DarkModeToggle'
import { Menu, Sun, X, Shield } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { signOut } from 'firebase/auth'
import { auth } from '../../lib/firebase'
import { ROLES } from '../../lib/roles'

export default function Layout({ children }) {
  const { user, role, roleLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)

  const isEmployeeArea = location.pathname.startsWith('/employee')
  const isAdmin = role === ROLES.ADMIN
  const isEmployeeRole =
    role === ROLES.EMPLOYEE || role === ROLES.MANAGER || role === ROLES.ADMIN

  const navItems = useMemo(() => {
    if (isEmployeeArea) {
      if (roleLoading) {
        return [
          { label: 'Checking access…', to: location.pathname, disabled: true }
        ]
      }

      if (!isEmployeeRole) {
        return [
          {
            label: 'Employee access unavailable',
            to: '/login/employee',
            disabled: true
          }
        ]
      }

      const items = [
        { label: 'Overview', to: '/employee/dashboard' },
        { label: 'Follow-Ups', to: '/employee/follow-ups' },
        { label: 'Clients', to: '/employee/clients' },
        { label: 'Calendar', to: '/employee/calendar' },
        { label: 'Analytics', to: '/employee/analytics' }
      ]
      
      // Add Admin link only if user is admin
      if (isAdmin) {
        items.push({ 
          label: 'Admin', 
          to: '/admin',
          icon: Shield,
          adminOnly: true
        })
      }
      
      return items
    }

    const items = [
      { label: 'Home', to: '/' },
      { label: 'Calculator', to: '/calculator' },
      { label: 'Schedule Estimate', to: '/schedule-estimate' }
    ]

    if (!user) {
      items.push({ label: 'Why SolarFi?', to: '/about' })
      items.push({ label: 'Contact', to: '/contact' })
      return items
    }

    if (roleLoading) {
      items.push({ label: 'Loading account…', to: location.pathname, disabled: true })
      return items
    }

    if (role === ROLES.CLIENT) {
      items.push({ label: 'Client Dashboard', to: '/client/dashboard' })
    } else if (isEmployeeRole) {
      items.push({ label: 'Employee Portal', to: '/employee/dashboard' })
      if (isAdmin) {
        items.push({
          label: 'Admin',
          to: '/admin',
          icon: Shield,
          adminOnly: true
        })
      }
    }

    return items
  }, [isEmployeeArea, user, roleLoading, role, isEmployeeRole, isAdmin, location.pathname])

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileOpen])

  const handleNavigate = (route) => {
    navigate(route)
    setMobileOpen(false)
  }

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <header className="sticky top-0 z-50 w-full border-b border-border bg-white/95 dark:bg-slate-950/80 backdrop-blur-sm supports-[backdrop-filter]:bg-white/95 dark:supports-[backdrop-filter]:bg-slate-950/75 transition-colors">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5 group" aria-label="SolarFi Home">
              <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-yellow-600 dark:bg-yellow-500 group-hover:bg-yellow-700 dark:group-hover:bg-yellow-600 transition-colors" aria-hidden="true">
                <Sun className="h-5 w-5 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-foreground">
                  SolarFi
                </span>
                <p className="text-[10px] text-muted-foreground hidden sm:block leading-tight">Energy Solutions</p>
              </div>
            </Link>
            
            <nav className="hidden md:flex items-center gap-2" role="navigation" aria-label="Main navigation">
              {navItems.map((item) => {
                const Icon = item.icon
                const isAdminLink = item.adminOnly
                const baseClasses = `rounded-full px-4 py-2 text-sm font-semibold transition-colors flex items-center gap-2 ${
                  isActive(item.to)
                    ? 'bg-amber-200/80 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200 border border-amber-400/40 shadow-sm'
                    : isAdminLink
                    ? 'text-purple-600 hover:text-purple-700 hover:bg-purple-100/70 dark:text-purple-400 dark:hover:text-purple-300 dark:hover:bg-purple-950/60 border border-purple-200/50 dark:border-purple-800/50'
                    : 'text-neutral-900 hover:text-neutral-950 hover:bg-amber-100/70 dark:text-neutral-200 dark:hover:text-white dark:hover:bg-neutral-800/60'
                }`

                const content = (
                  <>
                    {Icon && <Icon className="h-4 w-4" />}
                    {item.label}
                  </>
                )

                if (item.disabled) {
                  return (
                    <span
                      key={`${item.to}-${item.label}`}
                      className={`${baseClasses} cursor-not-allowed opacity-70`}
                    >
                      {content}
                    </span>
                  )
                }

                return (
                  <Link
                    key={`${item.to}-${item.label}`}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className={baseClasses}
                    aria-current={isActive(item.to) ? 'page' : undefined}
                  >
                    {content}
                  </Link>
                )
              })}
            </nav>
            
            <div className="flex items-center gap-2.5">
              <DarkModeToggle />
              {user ? (
                <>
                  <span className="hidden sm:inline text-sm text-muted-foreground" aria-label={`Signed in as ${user.displayName || user.email?.split('@')[0] || 'User'}`}>
                    {user.displayName || user.email?.split('@')[0] || 'User'}
                  </span>
                  <Button 
                    variant="outline"
                    onClick={async () => {
                      await signOut(auth)
                      navigate('/')
                    }}
                    className="text-sm px-4 py-2"
                    aria-label="Sign out"
                  >
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/login/client')}
                    className="hidden sm:inline-flex text-sm"
                  >
                    Login
                  </Button>
                  <Button 
                    onClick={() => navigate('/register')}
                    className="bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-500 dark:hover:bg-yellow-600 text-white border-0 shadow-sm hover:shadow-md transition-all text-sm px-4 py-2"
                  >
                    Get Started
                  </Button>
                </>
              )}
              <button
                type="button"
                className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-border hover:bg-muted/80 transition"
                onClick={() => setMobileOpen((prev) => !prev)}
                aria-label="Toggle navigation menu"
              >
                {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`md:hidden border-t border-border bg-background/98 backdrop-blur-sm transition-[max-height] duration-300 ease-in-out overflow-hidden ${
            mobileOpen ? 'max-h-[420px]' : 'max-h-0'
          }`}
          role="navigation"
          aria-label="Mobile navigation"
          aria-hidden={!mobileOpen}
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-4">
            <div className="grid gap-2">
              {navItems.map((item) => {
                const Icon = item.icon
                const isAdminLink = item.adminOnly
                const baseClasses = `w-full text-left rounded-lg px-4 py-3 text-sm font-semibold transition-colors flex items-center gap-2 ${
                  isActive(item.to)
                    ? 'bg-amber-200/80 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200 border border-amber-400/40 shadow-sm'
                    : isAdminLink
                    ? 'text-purple-600 hover:text-purple-700 hover:bg-purple-100/70 dark:text-purple-400 dark:hover:text-purple-300 dark:hover:bg-purple-950/60 border border-purple-200/50 dark:border-purple-800/50'
                    : 'text-neutral-900 hover:text-neutral-950 hover:bg-amber-100/70 dark:text-neutral-200 dark:hover:text-white dark:hover:bg-neutral-800/60'
                }`

                const content = (
                  <>
                    {Icon && <Icon className="h-4 w-4" />}
                    {item.label}
                  </>
                )

                if (item.disabled) {
                  return (
                    <div
                      key={`${item.to}-${item.label}`}
                      className={`${baseClasses} cursor-not-allowed opacity-70`}
                    >
                      {content}
                    </div>
                  )
                }

                return (
                  <button
                    key={`${item.to}-${item.label}`}
                    onClick={() => handleNavigate(item.to)}
                    className={baseClasses}
                  >
                    {content}
                  </button>
                )
              })}
            </div>
            {!user ? (
              <div className="grid gap-2">
                <Button variant="outline" className="w-full" onClick={() => handleNavigate('/login/client')}>
                  Login
                </Button>
                <Button className="w-full bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-500 dark:hover:bg-yellow-600" onClick={() => handleNavigate('/register')}>
                  Get Started
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                className="w-full"
                onClick={async () => {
                  await signOut(auth)
                  handleNavigate('/')
                }}
              >
                Sign Out
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">
        {children !== undefined ? children : <Outlet />}
      </main>

      <footer className="border-t border-border bg-background mt-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-yellow-600 dark:bg-yellow-500">
                  <Sun className="h-4 w-4 text-white" />
                </div>
                <span className="text-lg font-bold text-foreground">
                  SolarFi
                </span>
              </div>
              <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
                Your trusted partner for Tesla Powerwall installations and sustainable energy solutions. 
                Empowering homes with clean, renewable solar energy.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-sm mb-4">Company</h3>
              <ul className="space-y-2.5 text-sm">
                <li>
                  <Link to="/about" className="text-muted-foreground hover:text-foreground transition-colors">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-muted-foreground hover:text-foreground transition-colors">
                    Contact
                  </Link>
                </li>
                <li>
                  <Link to="/calculator" className="text-muted-foreground hover:text-foreground transition-colors">
                    Calculator
                  </Link>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-sm mb-4">Legal</h3>
              <ul className="space-y-2.5 text-sm">
                <li>
                  <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">
              © 2024 SolarFi. All rights reserved.
            </p>
            <div className="text-xs text-muted-foreground">
              <span>Made with care for a sustainable future</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
} 